import { yupResolver } from '@hookform/resolvers/yup'
import { lazyLoad } from '@if/core/utils'
import * as yup from '@if/core/yup'
import {
  ControlledToggleButtonGroup,
  UncontrolledTextFieldDebounced,
  useTranslatedToggleButtonOptionEnum,
} from '@if/mui'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, Toolbar, Typography } from '@mui/material'
import { EInteracWireStatus } from '@src/types/interac'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export interface InteracHistoryManualFiltersType {
  status: EInteracWireStatus.Matched | EInteracWireStatus.Discarded
  clientNameContains: string | null
  email: string | null
}

enum ESearchType {
  ClientName = 'clientName',
  Email = 'email',
}
const getESearchTypeList = lazyLoad(() => Object.values(ESearchType))

const historyTabs = [EInteracWireStatus.Matched, EInteracWireStatus.Discarded] as const
const getHistoryTabList = lazyLoad(() => [...historyTabs])

interface InteracHistoryFiltersProps {
  onFiltersChange: (filters: InteracHistoryManualFiltersType) => void
  defaultValues?: InteracHistoryManualFiltersType | null
}

const InteracHistoryFilters = ({ onFiltersChange, defaultValues: pDefaultValues }: InteracHistoryFiltersProps) => {
  const { t } = useTranslation()
  const resolver = useMemo(
    () =>
      yupResolver(
        yup.object({
          search: yup
            .string()
            .nullable()
            .when('searchType', {
              is: ESearchType.Email,
              then: (schema) => schema.email(),
            }),
          searchType: yup.mixed<ESearchType>().oneOf(Object.values(ESearchType)).default(ESearchType.ClientName),
          status: yup
            .mixed<EInteracWireStatus.Matched | EInteracWireStatus.Discarded>()
            .oneOf([EInteracWireStatus.Matched, EInteracWireStatus.Discarded])
            .default(EInteracWireStatus.Matched),
        }),
      ),
    [],
  )
  const defaultValues = useMemo(
    () => ({
      search: pDefaultValues?.clientNameContains ?? pDefaultValues?.email ?? null,
      searchType: pDefaultValues?.email ? ESearchType.Email : ESearchType.ClientName,
      status: pDefaultValues?.status ?? EInteracWireStatus.Matched,
    }),
    [pDefaultValues?.clientNameContains, pDefaultValues?.email, pDefaultValues?.status],
  )

  const { subscribe, control } = useForm({
    mode: 'onChange',
    resolver,
    defaultValues,
  })

  useEffect(() => {
    const cancelSubscription = subscribe({
      formState: {
        isValid: true,
        values: true,
      },
      callback: ({ values, isValid }) => {
        if (isValid) {
          const filters: InteracHistoryManualFiltersType = {
            status: values.status,
            clientNameContains: null,
            email: null,
          }
          if (values.search) {
            if (values.searchType === ESearchType.Email) {
              filters.email = values.search
            } else if (values.searchType === ESearchType.ClientName) {
              filters.clientNameContains = values.search
            }
          }
          onFiltersChange(filters)
        }
      },
    })
    return () => {
      cancelSubscription()
    }
  }, [subscribe, onFiltersChange])

  const historyTabOptions = useTranslatedToggleButtonOptionEnum('enum.interacHistoryTab', getHistoryTabList())
  const searchTypeOptions = useTranslatedToggleButtonOptionEnum('enum.searchType', getESearchTypeList())

  return (
    <Toolbar sx={{ gap: 1, pb: 1, alignItems: 'start' }}>
      <div>
        <Typography variant="subtitle2" style={{ visibility: 'hidden' }}>
          &nbsp;
        </Typography>
        <UncontrolledTextFieldDebounced
          control={control}
          name="search"
          fullWidth={false}
          sx={{ width: 300 }}
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </div>
      <div>
        <Typography variant="subtitle2">{t('common.searchType')}</Typography>
        <ControlledToggleButtonGroup
          control={control}
          name="searchType"
          exclusive
          color="primary"
          size="small"
          options={searchTypeOptions}
        />
      </div>
      <div>
        <Typography variant="subtitle2">{t('interacHistory.tab')}</Typography>
        <ControlledToggleButtonGroup
          control={control}
          name="status"
          exclusive
          size="small"
          options={historyTabOptions}
          color="primary"
        />
      </div>
    </Toolbar>
  )
}

export default InteracHistoryFilters
